import { useSettings } from '@/data/Settings';
import { ORDER_FLOW_SOURCE_STATE_KEY } from '@/data/constants/hdmDataKey';
import { ORDER_FLOW_SOURCE } from '@/data/constants/order';
import { getStateUpdater, useInitState, useSetState, useTrackedState } from '@/data/state/provider';
import { getStateKey } from '@/data/utils/getStateKey';
import { useCallback, useEffect, useMemo } from 'react';

export type OrderFlowSource = (typeof ORDER_FLOW_SOURCE)[keyof typeof ORDER_FLOW_SOURCE];

export type OrderFlowSourceState = {
	orderFlowSource: OrderFlowSource;
};

const ORDER_FLOW_SOURCE_BASE: OrderFlowSourceState = {
	orderFlowSource: ORDER_FLOW_SOURCE.CART,
};

/**
 * Which order page the shopper is on, cart or checkout.
 *
 * `useCart` is shared by both pages, so it cannot tell from its own scope which one is calling it
 * -- and some cart operations need to behave differently in checkout, where shipping and taxes
 * have already been calculated. Each page declares itself on mount (see `useOrderFlowSourceDeclare`)
 * and anything downstream of `useCart` can read the answer here.
 *
 * Defaults to `CART`, the more conservative of the two: it is the payload shape the cart page has
 * always sent.
 */
export const useOrderFlowSourceState = () => {
	const { settings } = useSettings();
	const key = useMemo(() => getStateKey(ORDER_FLOW_SOURCE_STATE_KEY, settings), [settings]);
	const baseState = useInitState(key, ORDER_FLOW_SOURCE_BASE);
	const orderFlowSourceUpdater = useMemo(
		() => getStateUpdater({ key, baseState }),
		[baseState, key]
	);

	const setState = useSetState();
	const fullState = useTrackedState();
	const { orderFlowSource } = (fullState[key] as OrderFlowSourceState) || baseState;

	const setOrderFlowSource = useCallback(
		(orderFlowSource: OrderFlowSource) =>
			orderFlowSourceUpdater({ setState, now: (current) => ({ ...current, orderFlowSource }) }),
		[setState, orderFlowSourceUpdater]
	);

	return { orderFlowSource, setOrderFlowSource };
};

/**
 * Declares the page the caller belongs to, for the lifetime of that page. Call it once from the
 * cart page and once from the checkout page; both routes are mutually exclusive, so only one
 * declaration is ever live.
 */
export const useOrderFlowSourceDeclare = (source: OrderFlowSource) => {
	const { setOrderFlowSource } = useOrderFlowSourceState();

	useEffect(() => {
		setOrderFlowSource(source);
	}, [source, setOrderFlowSource]);
};
